import { convertNumberToDollarFormat } from '@m/utils/convertNumberToDollarFormat';

import type {
  MonthlyPlanChoice,
  SiteWithHoursAndRate,
  SubscriptionPlan,
  SubscriptionSite,
} from '@m/types/api';

export type ParkNowParkingMode = 'on-demand' | 'monthly';

export const PARKING_MODE_QUERY = 'parking';

export function parseParkingMode(search: string): ParkNowParkingMode {
  const params = new URLSearchParams(search);
  return params.get(PARKING_MODE_QUERY) === 'monthly' ? 'monthly' : 'on-demand';
}

/** Search string without a leading `?`. Monthly is `parking=monthly`; on-demand omits the param. */
export function withParkingMode(search: string, mode: ParkNowParkingMode): string {
  const params = new URLSearchParams(search);
  if (mode === 'monthly') {
    params.set(PARKING_MODE_QUERY, 'monthly');
  } else {
    params.delete(PARKING_MODE_QUERY);
  }
  return params.toString();
}

export type SubscriptionSearchPayload = {
  existingSubscriptions?: unknown;
  recommendedSites?: unknown;
  site?: unknown;
  sites?: SubscriptionSite[] | null;
};

type MonthlyRateDetails = {
  baseRateMonthly?: number | null;
  monthlyRateWithFees?: number | null;
  totalMonthlyRate?: number | null;
};

type MonthlyRatePlan = {
  details?: MonthlyRateDetails | null;
};

export type SubscriptionSiteAddressFields = {
  state?: string | null;
  stateCode?: string | null;
  street?: string | null;
  streetAddress?: string | null;
  zip?: string | null;
  zipcode?: string | null;
};

type CustomerSubscriptionForSite = {
  site: { id: number };
  subscription: { status?: { isCancelled?: boolean } | null };
};

function firstNonBlank(...values: (string | null | undefined)[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
  }
  return undefined;
}

/** Keep only `sites` from GET /subscription/search. */
export function selectSubscriptionSearchSites(
  payload: SubscriptionSearchPayload | null | undefined,
): SubscriptionSite[] {
  if (!payload || !Array.isArray(payload.sites)) {
    return [];
  }
  return payload.sites;
}

export function getPlanMonthlyRateCents(plan: MonthlyRatePlan): number | null {
  const cents =
    plan.details?.totalMonthlyRate ??
    plan.details?.monthlyRateWithFees ??
    plan.details?.baseRateMonthly;
  return typeof cents === 'number' && Number.isFinite(cents) ? cents : null;
}

export function getLowestMonthlyPriceDollars(plans: MonthlyRatePlan[]): number | null {
  let lowestCents: number | null = null;
  for (const plan of plans) {
    const cents = getPlanMonthlyRateCents(plan);
    if (cents == null) {
      continue;
    }
    if (lowestCents == null || cents < lowestCents) {
      lowestCents = cents;
    }
  }
  return lowestCents == null ? null : lowestCents / 100;
}

export function formatMonthlyPinPrice(plans: MonthlyRatePlan[]): string | null {
  const dollars = getLowestMonthlyPriceDollars(plans);
  if (dollars == null) {
    return null;
  }
  const formatted = convertNumberToDollarFormat({ price: dollars });
  return formatted ? `${formatted}/mo` : null;
}

/**
 * MembershipDetailCard reads `totalMonthlyRate` before the other rate fields.
 * Copy a missing total from the same fallback chain without mutating the cached plan.
 */
export function withResolvedMonthlyRate<T extends MonthlyRatePlan>(plan: T): T {
  const cents = getPlanMonthlyRateCents(plan);
  if (cents == null || plan.details?.totalMonthlyRate != null) {
    return plan;
  }
  return {
    ...plan,
    details: {
      ...plan.details,
      totalMonthlyRate: cents,
    },
  };
}

/**
 * Detail card address uses `streetAddress`, `zipcode`, and `stateCode`.
 * Subscription search sites often send `street`, `zip`, and `state` instead.
 */
export function mapSubscriptionSiteForDetailCard<T extends SubscriptionSiteAddressFields>(
  site: T,
): T {
  return {
    ...site,
    streetAddress: firstNonBlank(site.streetAddress, site.street),
    zipcode: firstNonBlank(site.zipcode, site.zip),
    stateCode: firstNonBlank(site.stateCode, site.state),
  };
}

export function customerHasActiveSubscriptionForSite(
  subscriptions: CustomerSubscriptionForSite[] | undefined,
  siteId: number | undefined,
): boolean {
  if (siteId == null || !subscriptions) {
    return false;
  }
  return subscriptions.some(
    (entry) => entry.site.id === siteId && entry.subscription.status?.isCancelled !== true,
  );
}

export function readMonthlyPlanChoices(
  site: Pick<SiteWithHoursAndRate, 'monthlyPlanChoices'>,
): MonthlyPlanChoice[] {
  return site.monthlyPlanChoices ?? [];
}

function formatPlanAmount(cents: number): string | undefined {
  return (
    convertNumberToDollarFormat({ price: cents / 100, includeCommas: true, includeZeros: true }) ??
    undefined
  );
}

function finiteCoordinate(value: unknown): number | undefined {
  let number = Number.NaN;
  if (typeof value === 'number') {
    number = value;
  } else if (typeof value === 'string' && value.trim() !== '') {
    number = Number(value);
  }
  return Number.isFinite(number) ? number : undefined;
}

/**
 * Subscription search sites send `lat`/`lng` (what the old membership map plotted).
 * Some payloads also include `latitude`/`longitude` as 0. Prefer a real coordinate
 * so those sites are not placed at the equator and then clipped off the map.
 */
function pickCoordinate(primary: unknown, fallback: unknown): number | undefined {
  const first = finiteCoordinate(primary);
  const second = finiteCoordinate(fallback);
  if (first != null && first !== 0) return first;
  if (second != null && second !== 0) return second;
  return first ?? second;
}

/**
 * Shape a subscription-search site so the discovery list and map can render it.
 * Price is the lowest pre-tax monthly rate. These are not reservation inventory.
 */
export function subscriptionSiteToDisplaySite(entry: SubscriptionSite): SiteWithHoursAndRate {
  const site = mapSubscriptionSiteForDetailCard(entry.site);
  const plans = resolveSubscriptionPlans(entry.subscriptionPlans);
  const dollars = getLowestMonthlyPriceDollars(plans);
  const priceFormatted =
    dollars == null
      ? undefined
      : (convertNumberToDollarFormat({ price: dollars, includeCommas: true, includeZeros: true }) ??
        undefined);
  // Map pins show whole dollars and never "From", so they stay short.
  const pinPriceFormatted =
    dollars == null
      ? undefined
      : (convertNumberToDollarFormat({ price: Math.round(dollars), includeCommas: true }) ??
        undefined);
  const lat = pickCoordinate(site.lat, site.latitude);
  const lng = pickCoordinate(site.lng, site.longitude);
  const monthlyPlanChoices: MonthlyPlanChoice[] = plans.flatMap((plan) => {
    const cents = getPlanMonthlyRateCents(plan);
    const amountLabel = cents == null ? undefined : formatPlanAmount(cents);
    if (!amountLabel) return [];
    return [
      {
        amountLabel,
        displayText: plan.details?.displayText?.trim() || 'Monthly',
        id: plan.id,
        uuid: plan.uuid,
      },
    ];
  });
  return {
    ...site,
    lat,
    lng,
    latitude: lat,
    longitude: lng,
    allHoursOfOperation: site.allHoursOfOperation ?? [],
    firstPartyReservationsEnabled: false,
    monthlyPinPriceFormatted: pinPriceFormatted,
    monthlyPlanChoices,
    monthlyPriceFormatted:
      priceFormatted && monthlyPlanChoices.length > 1 ? `From ${priceFormatted}` : priceFormatted,
    monthlyRateLabel: 'per month',
  } as unknown as SiteWithHoursAndRate;
}

export function resolveSubscriptionPlans(
  plans: SubscriptionPlan[] | undefined,
): SubscriptionPlan[] {
  return (plans ?? [])
    .map((plan) => withResolvedMonthlyRate(plan))
    .sort((left, right) => {
      const leftCents = getPlanMonthlyRateCents(left);
      const rightCents = getPlanMonthlyRateCents(right);
      if (leftCents == null && rightCents == null) {
        return 0;
      }
      if (leftCents == null) {
        return 1;
      }
      if (rightCents == null) {
        return -1;
      }
      return leftCents - rightCents;
    });
}
